import Button from '@berlitz/button'
import Pagination from '@berlitz/pagination'
import Spacer from '@berlitz/spacer'
import Play from '@berlitz/streamline-icons/lib/regular/15-Video-Movies-TV/06-Controls/ControlsPlay'
import EmptyState from '@components/EmptyState'
import VideoModal from '@components/Modal/VideoModal'
import Table, { TableLoader } from '@components/Table'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { GET_RECORDINGS } from '@queries/recordings'
import { DATE_FORMAT } from '@utils/constants'
import React, { useState } from 'react'
import { Query } from 'react-apollo'
import { FormattedMessage, useIntl } from 'react-intl'
import { PaginationContainer } from './style'
import useTimeFormat from '@hooks/useTimeFormat'

interface IRecordingsTable {
  roomId: string
  endDate?: number
  startDate?: number
  provider?: string
}

const RecordingsTable: React.FC<IRecordingsTable & WithUserTimeProps> = ({
  endDate,
  startDate,
  roomId,
  toUserDate,
  provider = 'Kaltura',
}) => {
  const intl = useIntl()
  const { convertToUserTimeFormats } = useTimeFormat()
  const [page, setPage] = useState<number | null>(1)
  const [open, setOpen] = useState(false)
  const [video, setVideo] = useState({ src: '', title: '' })
  const generateTitle = (date) =>
    convertToUserTimeFormats(toUserDate((date * 1000).toString(), 'x', DATE_FORMAT.COMPLETE), DATE_FORMAT.COMPLETE)
  const fallbackProvider = provider || 'Kaltura'
  const PAGE_SIZE = 10
  return (
    <Query
      query={GET_RECORDINGS}
      variables={{
        provider: fallbackProvider.toLowerCase(),
        endDate,
        startDate,
        roomId,
        page,
      }}
      fetchPolicy="network-only"
    >
      {(res) => {
        const { data, loading, error } = res
        if (loading) {
          return <TableLoader />
        }

        if (error) {
          return (
            <Spacer>
              <EmptyState heading="Error fetching recordings." img="/images/lost.svg" />
            </Spacer>
          )
        }

        if (!data?.recordings?.recordings?.length) {
          return (
            <Spacer>
              <EmptyState heading="No recordings found." img="/images/lost.svg" />
            </Spacer>
          )
        }

        const columns = [
          {
            title: intl.formatMessage({ id: 'Date' }),
            dataIndex: 'startDate',
            key: 'startDate',
            align: 'left',
            render: (date: number) => <>{generateTitle(date)}</>,
          },
          {
            title: '',
            dataIndex: 'url',
            key: 'recording',
            align: 'right',
            render: (url: string, details) => {
              const urlByProvider = `${url}${provider == 'kaltura' ? '/flavorParamId/7' : ''}`
              return (
                <>
                  {url && (
                    <Button
                      icon={<Play size="xs" color="white" />}
                      onClick={() => {
                        setVideo({
                          src: urlByProvider,
                          title: generateTitle(details.startDate),
                        })
                        setOpen(true)
                      }}
                    >
                      <FormattedMessage id="Recording" />
                    </Button>
                  )}
                </>
              )
            },
          },
        ]

        const totalPages = Math.ceil(data?.recordings?.count / PAGE_SIZE)

        return (
          <>
            {data?.recordings?.count && (
              <>
                <Table columns={columns} dataSource={data?.recordings?.recordings} />
                {totalPages > 1 && (
                  <PaginationContainer>
                    <Pagination
                      activePage={page || 1}
                      numberPages={totalPages}
                      onChange={(pageNumber) => {
                        setPage(pageNumber)
                      }}
                      prevLabel={intl.formatMessage({ id: 'Back' })}
                      nextLabel={intl.formatMessage({ id: 'Next' })}
                    />
                  </PaginationContainer>
                )}
                <VideoModal
                  open={open}
                  onClose={() => setOpen(false)}
                  src={video.src}
                  title={video.title}
                  playing
                  withDownloadLink
                />
              </>
            )}
          </>
        )
      }}
    </Query>
  )
}

export default WithUserTimeHOC(RecordingsTable)
